import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EmeraldIcon } from '@emerald-react/icon';
import { EmeraldProgressBar } from '@emerald-react/progress-indicator';
import { useApi } from '@backstage/core-plugin-api';
import cx from 'classnames';
import { CoreEngineService } from './../core_engine';
import { promptApiRef } from '../../../apis/promptManagementApi';
import { useUserProfile } from '../../utils/useGetUserProfile';
import { AuthContext } from '../../../providers/AuthProvider';
import { emitter, updateIFrameData } from './../constants';
import './../layout/style.css';
import {
  DesignToCodeApi,
  designToCodeRef,
} from '../../../apis/designToCodeConfigApi';
import { FrameworkSelection } from './FrameworkSelection';
import { GeneratedCodeDisplay } from './GeneratedCodeDisplay';
import { PromptResponse } from './prompt-response';
import { generateId } from '../../utils/Utils';
import { FrontendConfigContext } from '../../../providers/FrontendConfigProvider';
import { EmeraldModal } from '@emerald-react/modal';
import TextToPrototype from './TextToPrototype';
import { figmaJsonToHtmlCss } from './figmaToHtmlCss';

interface PageContentProps {
  docId?: string;
}

const PageContent = ({ docId }: PageContentProps) => {
  const [result, setResult] = useState<any>(null);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const coreEngine: CoreEngineService = new CoreEngineService();
  const id = useMemo(() => generateId(), []);
  const [frameworkSelected, setFrameworkSelected] = useState<string>();
  const [frameworkVersionSelected, setFrameworkVersionSelected] =
    useState<string>('');
  const [frameworkStyleSelected, setFrameworkStyleSelected] =
    useState<string>();
  const promptApi = useApi(promptApiRef);
  const { accessToken } = useUserProfile();
  const { profEmail, profileName, isAdmin } = useContext(AuthContext);
  const [triggerDocumentCall, setTriggerDocumentCall] = useState(true);
  const [promptData, setPromptData] = useState<any>(null);

  const [minimisedDesign, setMinimisedDesign] = useState<boolean>(false);
  const [showTextToDesignModal, setShowTextToDesignModal] =
    useState<boolean>(false);
  const [showDesignEditModal, setShowDesignEditModal] =
    useState<boolean>(false);
  const documentResult = useRef<any>({});
  const currentDetails = useRef<any>({});

  const [loading, setLoading] = useState(false);
  const [designTitle, setDesignTitle] = useState<string>('');
  const [historyUpdateStartedForKey, setHistoryUpdateStartedForKey] =
    useState('');
  const designToCode: DesignToCodeApi = useApi(designToCodeRef);
  const { frontendConfigData } = useContext(FrontendConfigContext);

  let promptResponse: PromptResponse | null = null;
  const getPromptResponse = () => {
    if (!promptResponse) {
      promptResponse = new PromptResponse(
        promptApi,
        profEmail,
        accessToken,
        id,
        '07b6cb62-7f19-48d4-a896-249e449f23a0',
      );
    }
    return promptResponse;
  };

  const performMetrics = (data: any) => {
    setLoading(true);
    if (designToCode) {
      //designToCode.updateDesignMetrics(data);
      //console.log('Metrics data:', data);
      const payload: any = designToCode.getDefaultMetricsPayload();
      if (data?.filesWithFolders?.length) {
        const files: any[] = getListofFiles(data.filesWithFolders);
        //console.log('Files:', files);
        const nodes = documentResult.current?.content?.nodes || {};
        const fileKey = documentResult.current?.content?.fileKey || '';
        const fileName = (documentResult.current?.content?.name || '').replace(
          /[ ()]/g,
          '-',
        );
        const nodeKeys = Object.keys(nodes);
        const node = nodes[nodeKeys[0]]?.document || {};
        const workitemId = `https://www.figma.com/design/${fileKey}/${fileName}?node-id=${node.id?.replace(
          /:/g,
          '-',
        )}&m=dev`;

        payload.emailid = currentDetails.current.profEmail;
        payload.action = data.type;

        payload.additionaldata.content.description =
          payload.additionaldata.content.title =
            currentDetails.current.designTitle;

        payload.clientadditionaldata.workitemId = workitemId;
        payload.clientadditionaldata.workItemType = 'Figma Designs';

        const isReact =
          data.configSelection?.frameworkSelected?.toLowerCase() === 'react';
        payload.codeGenerationMetrics.framework =
          data.configSelection?.frameworkSelected || '';
        payload.codeGenerationMetrics.language = 'Typescript';
        payload.codeGenerationMetrics.style =
          data.configSelection?.frameworkStyleSelected || '';
        payload.codeGenerationMetrics.version =
          data.configSelection?.frameworkVersionSelected || '';
        payload.codeGenerationMetrics.figma_import_id = node.id || 0;
        payload.codeGenerationMetrics.session_id = data.sessionId;
        payload.codeGenerationMetrics.code_gen_id = data.code_gen_id || '0';

        payload.codeGenerationMetrics.totalFiles = files.length;
        payload.codeGenerationMetrics.totalLinesOfCode = 0;
        payload.codeGenerationMetrics.totalComponents = 0;
        files.forEach((each: any) => {
          payload.codeGenerationMetrics.totalLinesOfCode +=
            each.linesOfCode || 0;
          if (isReact) {
            if (
              (each.name.endsWith('.tsx') || each.name.endsWith('.jsx')) &&
              !(
                each.name.endsWith('.test.tsx') ||
                each.name.endsWith('.test.jsx')
              )
            ) {
              payload.codeGenerationMetrics.totalComponents++;
            }
          } else {
            if (each.name.endsWith('.ts') && !each.name.endsWith('.spec.ts')) {
              payload.codeGenerationMetrics.totalComponents++;
            }
          }
        });
        payload.codeGenerationMetrics.user_prompt =
          data.userPrompt || 'Default Prompt';

        if (data.type === 'Download') {
          payload.codeAcceptanceMetrics.session_id = data.sessionId;
          payload.codeAcceptanceMetrics.code_gen_id = data.code_gen_id || '0';
          payload.codeAcceptanceMetrics.accepted_id = data.sessionId;
          payload.codeAcceptanceMetrics.acceptance_type = data.type;
          payload.codeAcceptanceMetrics.lines_accepted =
            payload.codeGenerationMetrics.totalLinesOfCode;
          payload.codeAcceptanceMetrics.files_accepted = files.length;
        } else {
          payload.codeAcceptanceMetrics = {};
        }

        payload.dashboard.figma_import_id = 0;
        payload.dashboard.session_id = data.sessionId;
        payload.dashboard.framework =
          data.configSelection?.frameworkSelected || '';
        payload.dashboard.language = 'Typescript';
        payload.dashboard.style =
          data.configSelection?.frameworkStyleSelected || '';
        payload.dashboard.unitTests = 'jest';
        payload.dashboard.code_gen_id = data.code_gen_id || '0';
        payload.dashboard.totalLinesOfCode =
          payload.codeGenerationMetrics.totalLinesOfCode;
        payload.dashboard.totalFiles = files.length;
        payload.dashboard.totalComponents =
          payload.codeGenerationMetrics.totalComponents;
        payload.dashboard.user_name = currentDetails.current.profileName;
        payload.dashboard.user_email = currentDetails.current.profEmail;
        payload.dashboard.user_role = currentDetails.current.isAdmin
          ? 'admin'
          : 'user';
      }
      designToCode
        .updateDesignMetrics(payload)
        .then(resp => setLoading(false))
        .catch(err => {
          console.error('Error updating design metrics:', err);
          setLoading(false);
        });
      //console.log('Metrics Payload:', payload);
    } else {
      setLoading(false);
    }
  };

  const getListofFiles = (filesAndFolders: any[]) => {
    let files: any[] = [];
    filesAndFolders.forEach(each => {
      if (each.children?.length) {
        files = [...files, ...getListofFiles(each.children)];
      } else if (each.type === 'file') {
        each.linesOfCode =
          each.content
            ?.split('\n')
            ?.filter((line: string) => line.trim() !== '')?.length || 0;
        files.push(each);
      }
    });
    return files;
  };

  useEffect(() => {
    const handler = (data: any) => setLoading(data);
    emitter.on('showOrHideLoading', handler);
    emitter.on('updateHistory', updateGeneratedHistory.bind(this));
    emitter.on('performMetrics', performMetrics.bind(this));
    return () => {
      emitter.off('showOrHideLoading', handler);
      emitter.off('updateHistory', updateGeneratedHistory);
      emitter.off('performMetrics', performMetrics);
    };
  }, []);

  useEffect(() => {
    if (!frontendConfigData?.figma?.token) return;

    if (designToCode && !designToCode.isAuthorizationKeySet()) {
      designToCode.setAuthorizationKey(frontendConfigData?.figma?.token);
    }

    if (!promptResponse && promptApi && profEmail && accessToken) {
      promptResponse = getPromptResponse();
    }
    if (promptResponse && triggerDocumentCall && docId && !loading) {
      setTriggerDocumentCall(false);
      setLoading(true);
      currentDetails.current.profEmail = profEmail;
      currentDetails.current.profileName = profileName;

      designToCode
        .fetchDocuments(profEmail, profileName, null, docId)
        .then((result: any) => {
          if (result) {
            documentResult.current = result;
            parseTheContent(result.content);
          }
        })
        .catch((error: any) => {})
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    promptApi,
    profEmail,
    accessToken,
    id,
    promptResponse,
    docId,
    triggerDocumentCall,
    frontendConfigData,
  ]);

  useEffect(() => {
    currentDetails.current.isAdmin = isAdmin;
  }, [isAdmin]);

  useEffect(() => {
    if (historyUpdateStartedForKey) {
      const oldCodegenData: any[] =
        documentResult.current.content.codegenHistory[
          historyUpdateStartedForKey
        ].oldCodegenData;

      setHistoryUpdateStartedForKey('');
      updateDocumentData(
        oldCodegenData?.length
          ? oldCodegenData[oldCodegenData.length - 1].codeSection
          : null,
      );
    }
  }, [historyUpdateStartedForKey]);

  const parseTheContent = (_fileContent: any) => {
    const keys = _fileContent?.nodes ? Object.keys(_fileContent?.nodes) : [];
    const name =
      _fileContent?.selectedNodes?.[0]?.name || keys.length
        ? _fileContent?.nodes[keys[0]]?.document?.name
        : _fileContent?.name || 'Test_Project';
    const projectName = name
      ?.replace(/[^a-zA-Z0-9 ]/g, '') // Remove special characters
      ?.replace(/\s+/g, '_');
    setDesignTitle(name);
    currentDetails.current.designTitle = name;

    if (_fileContent?.selectedNodes) {
      let _result, _previewResult;
      if (_fileContent?.conversions?.initialConversion) {
        const { result, preview } = _fileContent.conversions.initialConversion;
        _result = result;
        _previewResult = preview;
        setResult(result);
        setPreviewResult(preview);
      } else {
        _result = coreEngine.htmlMain({
          sceneNode: _fileContent?.selectedNodes,
          globalTextStyleSegments: _fileContent?.globalTextStyleSegments,
        });
        setResult(_result);

        _previewResult = coreEngine.htmlMain({
          sceneNode: _fileContent?.selectedNodes,
          globalTextStyleSegments: _fileContent?.globalTextStyleSegments,
          selectionIconDataInfo: _fileContent?.selectionIconDataInfo,
          useSVGContent: true,
          useImageFileNameOnly: true,
          useInlineStyles: false,
        });
        setPreviewResult(_previewResult);
      }
      updateIFrameData(
        'preview_iFrame',
        _previewResult.html,
        _previewResult.css.join('\n') + '\n',
      );

      emitter.emit('codegen:html:css', _result);
      emitter.emit('assetsUpdate', {
        images: _previewResult.imageAndSVGMapping,
        projectName,
        isImportedDesign: false,
      });
      setTimeout(() => {
        updateGeneratedCode(_result, _previewResult);
      }, 100);
    } else if (_fileContent?.nodes) {
      setTimeout(async () => {
        let data: any = {};
        if (_fileContent?.conversions?.initialConversion) {
          const { result } = _fileContent.conversions.initialConversion;
          data = result;
          setResult(result);
        } else {
          setLoading(true);
          data = await figmaJsonToHtmlCss(
            _fileContent,
            _fileContent.fileKey,
            designToCode,
          );
          setResult(data);
        }

        emitter.emit('codegen:html:css', data);
        emitter.emit('assetsUpdate', {
          images: '',
          projectName,
          isImportedDesign: true,
        });
        updateIFrameData('preview_iFrame', data.html, data.css);
        setLoading(false);
        setTimeout(() => {
          updateGeneratedCode(data);
        }, 100);
      }, 100);
    }
    if (
      documentResult.current?.content?.codegenHistory &&
      Object.keys(documentResult.current.content.codegenHistory).length
    ) {
      const keys = Object.keys(documentResult.current.content.codegenHistory);
      emitter.emit(
        'historyFound',
        documentResult.current.content.codegenHistory,
      );
      emitter.emit('historySectionChange', { key: keys[0] });
    }
  };

  const updateGeneratedCode = (resultData: any, previewData: any = {}) => {
    documentResult.current.content.conversions =
      documentResult.current.content.conversions || {};
    documentResult.current.content.conversions.initialConversion = {
      result: resultData,
      preview: previewData,
    };
    updateDocumentData();
  };

  const updateGeneratedHistory = (data: any) => {
    documentResult.current.content.codegenHistory =
      documentResult.current.content.codegenHistory || {};
    documentResult.current.content.codegenHistory = data.history;

    setHistoryUpdateStartedForKey(data.key);
  };

  const updateDocumentData = (codeSection?: any) => {
    setLoading(true);
    designToCode
      .updateDocument(
        profEmail,
        profileName,
        null,
        docId,
        documentResult.current,
      )
      .then((result: any) => {
        if (codeSection) {
          emitter.emit('performMetrics', {
            ...codeSection,
            type: 'Generation',
          });
        } else {
          setLoading(false);
        }
      })
      .catch((error: any) => setLoading(false));
  };

  return (
    <div className="design-to-code-page-container">
      {loading && (
        <div className="loading-section">
          <EmeraldProgressBar determinate={false} />
        </div>
      )}
      <div className="page-navigation code-gen-page-navigation">
        {/* <EmeraldButton
          label="Text To Prototype"
          type="TextButton"
          onClick={() => setShowTextToDesignModal(true)}
        /> */}
      </div>
      <div className="page-codegen-panel">
        <FrameworkSelection
          key={id}
          frameworkSelected={frameworkSelected}
          frameworkVersionSelected={frameworkVersionSelected}
          frameworkStyleSelected={frameworkStyleSelected}
          setFrameworkSelected={setFrameworkSelected}
          setFrameworkVersionSelected={setFrameworkVersionSelected}
          setFrameworkStyleSelected={setFrameworkStyleSelected}
        ></FrameworkSelection>
        <GeneratedCodeDisplay
          promptData={promptData}
          setPromptData={setPromptData}
          promptResponse={promptResponse}
        ></GeneratedCodeDisplay>
        <div
          className={cx('page-design-preview-panel', {
            minimised: minimisedDesign,
          })}
        >
          <div className="design-preview-header">
            {minimisedDesign ? (
              <EmeraldIcon
                icon="right_panel_open"
                symbol={'right_panel_open'}
                variant={'symbol'}
                type="outlined"
                onClick={() => setMinimisedDesign(false)}
                color="#003f2d"
              />
            ) : (
              <EmeraldIcon
                icon="right_panel_close"
                symbol={'right_panel_close'}
                variant={'symbol'}
                type="outlined"
                onClick={() => setMinimisedDesign(true)}
                color="#003f2d"
              />
            )}
            <label className="design-title">{designTitle}</label>
            {/* <EmeraldIcon
              icon="edit"
              type="filled"
              onClick={() => setShowDesignEditModal(true)}
              color="#003f2d"
            /> // This will target on upcoming releases 
            */}
          </div>
          <iframe
            id="preview_iFrame"
            style={{ width: '100%', height: '100%' }}
          ></iframe>
        </div>
      </div>

      {(showTextToDesignModal || showDesignEditModal) && (
        <EmeraldModal
          opened={true}
          enableFullscreen
          hasCloseIcon
          onClose={() => {
            setShowTextToDesignModal(false);
            setShowDesignEditModal(false);
          }}
          headerText={showDesignEditModal ? 'Design Edit' : 'Text To Design'}
        >
          <TextToPrototype
            previewResult={showTextToDesignModal ? {} : previewResult || result}
            result={showTextToDesignModal ? {} : result}
          />
        </EmeraldModal>
      )}
    </div>
  );
};

export default PageContent;
